import type { GraphModification, ConceptFunction, Graph } from "@lr-notitia/notitia-engineer/schemas";
import type { PluginCompilerResponse } from "@lr-notitia/notitia-model-store/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-engineer/client";
import type { Terminology } from "@lr-notitia/notitia-dictionary/schemas";
import { handleGraphModification } from "./utils";
import { useSymbolsStore } from "../symbols";
import * as Metrics from "../metrics";

let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	symbolsStore = useSymbolsStore();
}

export const conceptFuncs = (graph: Ref<Graph>) =>
	computed<ConceptFunction[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "function" && node.layer === "concept"
			) as ConceptFunction[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(
					symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase()
				)
		);
	});
export function conceptFunc(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => {
			Metrics.recordAccess(id);
			return graph.value.nodes[id] as ConceptFunction | undefined;
		},
		add: (data: { refersTo: string; localTerminology: Terminology }) => add(domainId, graph, data),
		update: (func: ConceptFunction) => update(domainId, graph, func),
		remove: (id: string) => remove(domainId, graph, id),
	};
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	data: { refersTo: string; localTerminology: Terminology }
): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		api()
			.domain(domainId.value)
			.functions()
			.post(data)
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function update(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	func: ConceptFunction
): Promise<{ compilerResponse: PluginCompilerResponse; graphModification: GraphModification }> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[func.id]) return reject();
		api()
			.domain(domainId.value)
			.function(func.id)
			.put(func)
			.then((d) => {
				handleGraphModification(graph, d.graphModification);
				resolve(d);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.function(id)
			.del()
			.then((data) => {
				handleGraphModification(graph, data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
